import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useI18n } from '@udemy/i18n';
import { Button } from '@udemy/react-core-components';
import { Loader } from '@udemy/react-reveal-components';
import styles from "./stacked-cards.module.css";
const ShowMoreButton = ({
  onClick,
  loading
}) => {
  const {
    gettext
  } = useI18n();
  return /*#__PURE__*/React.createElement(Button, {
    onClick: onClick,
    disabled: loading,
    className: classNames(styles['show-more-button'], 'ud-btn ud-btn-medium ud-btn-secondary ud-heading-sm')
  }, loading && /*#__PURE__*/React.createElement(Loader, {
    color: "inherit",
    overlay: true,
    "data-testid": "loader-component"
  }), gettext('Show more'));
};
export const StackedCards = ({
  cards,
  onMore,
  maxCardsPerStack = 4,
  maxCards = 16,
  isLoading = false,
  hasMore = true,
  skeleton = null,
  showDividers = false,
  smallGap = false
}) => {
  // State to keep track of the intended next visible card count
  const [nextVisibleCardCount, setNextVisibleCardCount] = useState(0);

  // State to keep track of the number of cards currently visible
  const [visibleCardCount, setVisibleCardCount] = useState(Math.min(maxCardsPerStack, cards.length));
  useEffect(() => {
    // Update nextVisibleCardCount when cards change
    setNextVisibleCardCount(prevCount => {
      if (prevCount === 0 && cards.length > 0) {
        // If we previously had no cards and now we do, set to maxCardsPerStack
        return Math.min(maxCardsPerStack, cards.length);
      }
      // Otherwise, keep the current count
      return prevCount;
    });
  }, [cards, maxCardsPerStack]);
  useEffect(() => {
    // Update visibleCardCount based on nextVisibleCardCount and cards.length
    nextVisibleCardCount !== 0 && setVisibleCardCount(Math.min(nextVisibleCardCount, cards.length));
  }, [cards, nextVisibleCardCount]);
  const handleShowMore = () => {
    const prevVisibleCardCount = visibleCardCount;
    // Calculate the new visible card count, capped by maxCards
    const newVisibleCardCount = Math.min(visibleCardCount + maxCardsPerStack, maxCards);

    // Update the next visible card count
    setNextVisibleCardCount(newVisibleCardCount);
    if (prevVisibleCardCount === cards.length) {
      // If we've shown all available cards, call onMore to request more
      onMore(newVisibleCardCount, prevVisibleCardCount, cards.length);
    }
  };

  // Determine whether to show the "Show More" button
  //  Show button if there are more cards to display or if we can load more
  const showMoreButton = visibleCardCount < Math.min(cards.length, maxCards) || hasMore && visibleCardCount < maxCards;
  return /*#__PURE__*/React.createElement("div", {
    className: styles['stacked-cards'],
    "data-testid": 'stacked-cards'
  }, isLoading && cards.length === 0 ? skeleton : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['cards-list'], {
      [styles['cards-list-without-dividers']]: !showDividers,
      [styles['cards-list-small-gap']]: smallGap
    })
  }, cards.slice(0, visibleCardCount).map((card, index) => /*#__PURE__*/React.createElement("div", {
    className: classNames({
      [styles['stacked-card-with-divider']]: showDividers
    }),
    key: index
  }, card))), showMoreButton && /*#__PURE__*/React.createElement("div", {
    className: styles['show-more-container']
  }, /*#__PURE__*/React.createElement(ShowMoreButton, {
    onClick: handleShowMore,
    loading: isLoading
  }))));
};
//# sourceMappingURL=stacked-cards.react-component.js.map